import { memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
// i18next
// import i18n from 'i18next';
// import { useTranslation } from 'react-i18next';

// @mui
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  Input,
  InputAdornment,
  Slide,
  Stack,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';


// components

// store
import { SvgCloseFill, SvgSearchFill } from '../../components/SvgIcons';

import { bgBlur } from '../../utils/cssStyles';
import Iconify from '../../components/iconify';
import { HEADER } from '../../store/constant';
import MOCK_SEARCH from '../../_mock/search.mock';
import { updateSearchKey, updateSearchValue } from '../../store/actions/searchAction';
import { IconPopover } from '../@popover/IconPopover';


/* ======================================================================================= */


const StyledSearchbar = styled('div')(({ theme }) => ({
  ...bgBlur({ color: theme.palette.background.default }),
  top: 0,
  left: 0,
  zIndex: 99,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  height: HEADER.MOBILE_HEIGHT,
  padding: theme.spacing(0, 3),
  boxShadow: theme.customShadows.z8,
  [theme.breakpoints.up('md')]: {
    height: HEADER.DESKTOP_HEIGHT,
    padding: theme.spacing(0, 5),
  },
}));

const Searchbar = styled('div')(({ theme }) => ({
  width: 424,
  height: 40,
  display: 'flex',
  [theme.breakpoints.up('md')]: {
    height: 40,
  },
  '& .MuiInput-root': {
    background: '#F5F5F5',
    padding: theme.spacing(1.25, 1.5),
  }
}));

/* ======================================================================================= */

function GlobalSearchbar() {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        {!open && (
          <IconButton onClick={handleOpen}>
            <Iconify icon="eva:search-fill" />
          </IconButton>
        )}

        <Slide direction="down" in={open} mountOnEnter unmountOnExit>
          <StyledSearchbar>
            <Input
              autoFocus
              fullWidth
              disableUnderline
              placeholder="Search…"
              startAdornment={
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                </InputAdornment>
              }
              sx={{ mr: 1, fontWeight: 'fontWeightBold' }}
            />
            <Button variant="contained" onClick={handleClose}>
              Search
            </Button>
          </StyledSearchbar>
        </Slide>
      </div>
    </ClickAwayListener>
  );
}

/* ======================================================================================= */


const SearchPopover = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigator = useNavigate();

  const { search } = useSelector(state => state);

  const handleSearch = async () => {
    await navigator(`/honhai/search-result/full_text_search`);
    await dispatch(updateSearchKey(search?.value));
  };

  const handleClear = async () => {
    await dispatch(updateSearchKey(''));
  };

  return (
    <IconPopover
      position={{ x: '-414px' }}
      primary={<SvgSearchFill color={theme.palette.common.white} />}
      content={
        <Stack spacing={1.5} p={3}>
          <Searchbar>
            <Input
              autoFocus
              fullWidth
              // disableUnderline
              placeholder="輸入關鍵字以獲得感興趣的內容"
              value={search?.value}
              onInput={(event) => dispatch(updateSearchValue(event?.target?.value))}
              onKeyDown={async (event) => {
                if (event.key === 'Enter') {
                  await handleSearch();
                }
              }}
              endAdornment={
                <InputAdornment onClick={handleClear} position="start" sx={{ cursor: 'pointer', textAlign: 'center' }}>
                  {
                    (!!search?.value) ? (
                      <SvgCloseFill width={16} height={16} color={'common.black'} />
                    ) : ''
                  }
                </InputAdornment>
              }
              sx={{
                mr: 1,
                '&:after': {
                  borderBottomColor: theme => theme.palette.color.primaryBlue3
                },
                '&:before': {
                  borderBottomColor: 'transparent'
                },
                '&:hover:not(.Mui-disabled, .Mui-error), &.Mui-focused': {
                  '&:before': {
                    borderBottomColor: theme => theme.palette.color.primaryBlue3
                  },
                },
              }}
            />
            
            <Button
              onClick={handleSearch}
              color={'primary'}
              variant="contained"
              sx={{ borderRadius: 0, fontSize: 18, minWidth: 84 }}
            >
              搜尋
            </Button>
          </Searchbar>
          <Box>
            <Typography component={'span'} variant={'body1'} color={'text.secondary'}>近期熱搜：</Typography>
            {
              _.map(MOCK_SEARCH.hots, (item, index) => (
                <Typography
                  key={index}
                  onClick={async () => {
                    await navigator(`/honhai/search-result/full_text_search`);
                    await dispatch(updateSearchKey(item));
                  }}
                  component={'span'}
                  variant={'body1'}
                  fontWeight={700}
                  color={theme.palette.color.primaryBlue3}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    mr: 1.5,
                    transition: 'all ease 0.3s',
                    '&:hover': { textDecoration: 'underline' },
                    '&:not(:last-of-type)': {
                      '&:after': {
                        content:'"、"',
                        color: 'text.secondary',
                        position: 'absolute'
                      },
                    },
                  }}
                >
                  { item }
                </Typography>
              ))
            }
          </Box>
        </Stack>
      }
    />
  );
};

export default memo(SearchPopover);
